<template>
  <div class="relative select-none" ref="dropdownRef">
    <div class="flex items-center">
      <!-- Deselect button -->
      <button
        v-if="selectedUser"
        @click="handleUserDeselect"
        class="p-2 rounded-l-lg hover:bg-red-700 w-[40px] h-[40px] bg-red-600 flex items-center justify-center border-r border-neutral-300"
      >
        <X :size="18" class="text-white" />
      </button>

      <!-- Dropdown button -->
      <button
        ref="buttonRef"
        @click="toggleDropdown"
        class="font-medium text-sm px-4 py-2.5 text-center inline-flex items-center md:w-[160px] justify-between"
        :class="{ 'text-neutral-800 bg-neutral-100 hover:bg-neutral-200 rounded-lg w-[74px]': !selectedUser, 'text-white bg-neutral-800 hover:bg-neutral-700 rounded-r-lg w-[114px]': selectedUser }"
        type="button"
      >
        <div class="flex items-center">
          <UserPen
            :size="18"
            :stroke-width="1.75"
            class="mr-2"
          />
          <span v-if="selectedUser" class="md:hidden w-7">{{ initials }}</span>
          <span class="hidden truncate max-w-[80px] md:inline">{{
            displayName
          }}</span>
        </div>
        <svg
          class="w-2.5 h-2.5 flex-shrink-0"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
      </button>
    </div>

    <!-- Dropdown menu -->
    <div
      v-if="isOpen"
      ref="dropdownMenuRef"
      class="absolute right-0 z-40 mt-3 bg-white border border-solid divide-y rounded-lg shadow-lg border-neutral-300 divide-neutral-100"
    >
      <ul class="max-h-52 p-2 overflow-y-auto text-sm text-neutral-700 w-[190px]">
        <li v-for="user in users" :key="user.id">
          <div
            @click="handleUserSelect(user)"
            class="flex items-center px-2 py-2 rounded cursor-pointer hover:bg-neutral-100"
          >
            <span class="truncate"
              >{{ user.first_name }} {{ user.last_name }}</span
            >
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  defineEmits,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import { storeToRefs } from "pinia";
import { useUsersStore } from "@/stores/usersStore";
import { UserPen, X } from "lucide-vue-next";

// Define emits
const emit = defineEmits(["select-user"]);

// Initialize users store
const usersStore = useUsersStore();
const { users } = storeToRefs(usersStore);

// Reactive state
const isOpen = ref(false);
const dropdownRef = ref(null);
const buttonRef = ref(null);
const dropdownMenuRef = ref(null);
const selectedUser = ref(null);
const searchQuery = ref("");

// Computed property for display name
const displayName = computed(() => {
  if (!selectedUser.value) return "Vælg bruger";

  if (selectedUser.value.first_name && selectedUser.value.last_name) {
    return `${selectedUser.value.first_name} ${selectedUser.value.last_name}`.trim();
  } else if (selectedUser.value.first_name) {
    return selectedUser.value.first_name.trim();
  } else if (selectedUser.value.last_name) {
    return selectedUser.value.last_name.trim();
  } else if (selectedUser.value.roles && selectedUser.value.roles.length > 0) {
    return (
      selectedUser.value.roles[0].charAt(0).toUpperCase() +
      selectedUser.value.roles[0].slice(1)
    );
  } else {
    return "Bruger";
  }
});

// Computed property for user initials
const initials = computed(() => usersStore.getUserInitials(selectedUser.value));

// Method to toggle dropdown visibility
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

// Method to close dropdown
const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = "";
};

// Method to handle user selection
const handleUserSelect = (user) => {
  selectedUser.value = user;
  emit("select-user", user);
  closeDropdown();
};

// Method to handle user deselection
const handleUserDeselect = async () => {
  selectedUser.value = null;
  emit("select-user", null);
  await usersStore.fetchUsers();
};

// Method to handle outside click
const handleOutsideClick = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    closeDropdown();
  }
};

// Watch for changes in the users array and update the selected user
watch(users, (newUsers) => {
  if (selectedUser.value && newUsers.find((user) => user.id === selectedUser.value.id)) {
    selectedUser.value = newUsers.find((user) => user.id === selectedUser.value.id);
    emit("select-user", selectedUser.value);
  }
});

// Lifecycle hooks
onMounted(() => {
  document.addEventListener("click", handleOutsideClick);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleOutsideClick);
});
</script>